import React, { useState, useMemo, useCallback, useImperativeHandle, useRef, useEffect } from 'react';
import { Combobox as HeadlessCombobox } from '@headlessui/react'
import { Icon } from '@dspworkplace/ui';
// @ts-ignore
import style from './combobox.module.css';

import {DefaultOption} from './Options';
import { useVirtualizer } from '@tanstack/react-virtual';
import {usePopper} from "react-popper";

const availableSizes = {extraSmall: 80, small: 140, default: 300, big: 460};

export interface ComboboxPropsOption {
    label: string | number
    value: string | number | null
    disabled?: boolean

    [key: string]: any
}

export type OptionRenderProps = React.FC<{ row: ComboboxPropsOption, index: number }>

export interface ComboboxProps {
    label?: string;
    name?: string | null;
    size?: keyof typeof availableSizes | 'full' | number;
    options?: ComboboxPropsOption[];
    OptionRender?: OptionRenderProps | null;
    initialValue?: number | string;
    onChange?: Function;
    startOpen?: boolean;
    placeholder?: string;
    nullText?: string;
    hasError?: boolean;
    isAdd: boolean;
    optionStyle: object;
    isDisabled: boolean;
    autoFocus?: boolean;
    virtualizerScroll: boolean;
}

const findOptionByProp = (options: ComboboxPropsOption[number], prop: string, value: string | number | undefined): ComboboxPropsOption | null => {
    let first = 0, last = options.length - 1;
    const valueString = value ? value.toString() : '';
    while (first <= last) {
        if (options?.[first]?.[prop]?.toString() === valueString) {
            return options[first];
        }
        first++

        if (options[last] && options?.[last]?.[prop]?.toString() === valueString) {
            return options[last];
        }
        last--
    }

    return null
}

const Combobox = ({ label,name = null, size = 'default', options: paramOpt = [], OptionRender, initialValue, onChange, startOpen = false, autoFocus = false, placeholder, nullText, isAdd = true, hasError = false, optionStyle = { maxHeight: '218px' }, isDisabled = false, virtualizerScroll = false }: ComboboxProps, ref) => {
    const [selectedOption, setSelectedOption] = useState<ComboboxPropsOption | null>(() => {
        return findOptionByProp(paramOpt, 'value', initialValue)
    })
    const [query, setQuery] = useState('')
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const comboBtn = useRef()
    const parentRef = useRef(null);
    const combinedRef = useCallback((node) => {
        setPopperElement(node);
        parentRef.current = node;
    }, []);
    const virtualizerScrollStyle = virtualizerScroll ? { position: 'absolute', width: "100%" } : {};
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        //strategy: 'fixed',
        modifiers: [
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['top-start'],
                },
            },
        ],
    })

    useImperativeHandle(ref, () => ({
        reset: (value) => {
            setSelectedOption(() => findOptionByProp(paramOpt, 'value', value));
        }
    }));

    const LayoutOption = useMemo(() => {
        return OptionRender || DefaultOption
    }, [OptionRender])

    const options = useMemo(() => {
        if (!nullText) return paramOpt

        const optCopy = [...paramOpt]
        optCopy.unshift({label: nullText, value: null})
        return optCopy
    }, [nullText, paramOpt])

    const inlineStyle = useMemo(() => {
        let inlineStyle = {};
        if (size && size !== 'full') inlineStyle['width'] = typeof size === 'number' ? size + 'px' : availableSizes[size] + 'px';
        if (size === 'full') inlineStyle['height'] = '100%';
        return inlineStyle;
    }, [size])

    const handleChange = useCallback((selectedOption, emitEvent = true) => {
        setQuery('')
        setSelectedOption(selectedOption)
        if (emitEvent && onChange) {
            onChange(selectedOption);
        }
    }, [onChange])

    const handleActive = useCallback(({active}) => {
        return `${style.comboboxOption} ${active ? style.comboboxOptionActive : ''}`
    }, [])

    const handleActiveButton = useCallback(({open}) => {
        return open ? <Icon.ArrowUp/> : <Icon.ArrowDown/>
    }, [])

    const handleDisplayValue = (opt: ComboboxPropsOption) => {
        return opt?.label.toString()
    }

    const onInputChange = useCallback((event) => {
        setQuery(event.target.value.trim())
    }, [])

    const filteredOptions =
        query === ''
            ? options
            : options.filter((option) => {
                const label = option.label.toString();
                return label.toLowerCase().includes(query.toLowerCase())
            })

    useEffect(() => {
        if (startOpen) {
            comboBtn.current?.click();
        }
    }, [])

    const rowVirtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 35,
        overscan: 5,
    });

    useEffect(() => {
        if (virtualizerScroll && popperElement && rowVirtualizer) {
            requestAnimationFrame(() => {
                rowVirtualizer.scrollToOffset(0);
            });
        }
    }, [popperElement]);

    useEffect(() => {
        if (virtualizerScroll && parentRef.current && rowVirtualizer) {
            rowVirtualizer.scrollToOffset(0);
        }
    }, [query]);

    useEffect(() => {
        if (virtualizerScroll && selectedOption && filteredOptions.length > 0) {
            const selectedIndex = filteredOptions.findIndex(
                opt => opt.value === selectedOption.value
            );
            if (selectedIndex >= 0) {
                rowVirtualizer.scrollToIndex(selectedIndex, { align: 'center' });
            }
        }
    }, [selectedOption, filteredOptions]);

    return (
        <HeadlessCombobox
            value={selectedOption}
            onChange={handleChange}
            as={'div'}
            className={style.combobox}
            style={inlineStyle}
            nullable={true}
            name={name}
        >
            {label && <HeadlessCombobox.Label className={style.comboboxLabel}>{label}</HeadlessCombobox.Label>}
            <HeadlessCombobox.Button as={'div'} className={style.comboboxContent} ref={comboBtn}>
                <HeadlessCombobox.Input
                    onChange={onInputChange}
                    className={`${style.comboboxInput} ${size === 'full' ? style.comboboxInputFull : ''} ${hasError === true ? style.comboboxWithError : ''}`}
                    autoFocus={autoFocus}
                    displayValue={handleDisplayValue}
                    placeholder={placeholder}
                    ref={setReferenceElement}
                    autoComplete={'off'}
                    disabled={isDisabled}
                />
                <HeadlessCombobox.Options
                    className={style.comboboxOptions}
                    ref={virtualizerScroll ? combinedRef : setPopperElement}
                    style={
                        virtualizerScroll
                          ? { ...styles.popper, ...optionStyle, ...virtualizerScrollStyle }
                          : { ...styles.popper, ...optionStyle }
                      }
                    {...attributes.popper}
                >
                    {isAdd && query.length > 0 && (
                        <HeadlessCombobox.Option
                            value={{id: null, value: query, label: query}}
                            className={`${style.comboboxOptionCreate} ${style.comboboxOption}`}
                        >
                            <LayoutOption row={{value: query, label: `Create "${query}"`}}
                                          index={filteredOptions.length + 1}/>
                        </HeadlessCombobox.Option>
                    )}
                    {virtualizerScroll ? <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const option = filteredOptions[virtualRow.index];
                                return (
                                    <HeadlessCombobox.Option
                                        disabled={option.disabled}
                                        value={option}
                                        className={handleActive}
                                        key={option.value?.id}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: `${virtualRow.size}px`,
                                            transform: `translateY(${virtualRow.start}px)`,
                                        }}
                                    >
                                        <LayoutOption row={option} index={virtualRow.index} />
                                    </HeadlessCombobox.Option>
                                );
                            })}
                        </div> : filteredOptions.map((option, key) => (
                        <HeadlessCombobox.Option
                            disabled={option.disabled}
                            value={option}
                            key={option.value}
                            className={handleActive}
                        >
                            <LayoutOption row={option} index={key}/>
                        </HeadlessCombobox.Option>
                    ))}
                </HeadlessCombobox.Options>
                <HeadlessCombobox.Button className={style.comboboxButton}>
                    {handleActiveButton}
                </HeadlessCombobox.Button>
            </HeadlessCombobox.Button>
        </HeadlessCombobox>
    )
}
export default React.memo(React.forwardRef(Combobox));